/* eslint-disable no-empty-pattern */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

export const sendSlaEsignRequest = createAsyncThunk(
  "esign/sendSlaEsignRequest",
  async (location, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/investor/sendSlaESignRequest?location=${location}`);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendAgreementESignRequest = createAsyncThunk(
  "esign/sendAgreementESignRequest",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/investor/sendAgreementESignRequest?dealId=${payload.dealId}&dealDiscountAmt=${payload.dealDiscountAmt}&dealActualAmt=${payload.dealActualAmt}&location=${payload.location}`
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error || "An error occurred");
    }
  }
);

export const generateRiskAgreement = createAsyncThunk(
  "esign/generateRiskAgreement",
  async (dealId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/investor/generateRiskAgreement?dealId=${dealId}`
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendRiskAgreementRequest = createAsyncThunk(
  "esign/sendRiskAgreementRequest",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/investor/sendRiskAgreementRequest?dealId=${payload.dealId}`
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getSlaSignStatus = createAsyncThunk(
  "esign/getSlaSignStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/investor/getSlaSignStatus?document_id=${payload.document_id}&docket_id=${payload.docket_id}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getAgreementEsignStatus = createAsyncThunk(
  "esign/getAgreementEsignStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (payload?.type) params.append("type", payload.type);
      if (payload?.document_id) params.append("document_id", payload.document_id);
      if (payload?.docket_id) params.append("docket_id", payload.docket_id);
      if (payload?.dealId) params.append("dealId", payload.dealId);
      if (payload?.transactionId) params.append("transactionId", payload.transactionId);

      const response = await axios.get(
        `/api/investor/getAgreementEsignStatus?${params.toString()}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDocketInfo = createAsyncThunk(
  "esign/getDocketInfo",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/investor/getDocketInfo?document_id=${payload.document_id}&docket_id=${payload.docket_id}&dealId=${payload.dealId}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const EsignSlice = createSlice({
  name: "esign",
  initialState: {
    esignData: {},
    tripartyAgreeLoading: false,
    tripartyAgreeData: {},
    riskAgreementLoading: false,
    riskAgreement: {},
    riskAgreementData: {},
    agreementSubmitLoading: false,
    agreementSubmitError: null,
    docketInfoLoading: false,
    docketInfoError: null,
    loading: false,
    error: null,
    agreeReqError: null,
    checkStatusLoading: false,
    checkStatusError: null,
  },

  extraReducers: (builder) => {
    builder.addCase(sendSlaEsignRequest.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendSlaEsignRequest.fulfilled, (state, action) => {
      state.loading = false;
      state.esignData = action.payload;
      state.error = null;
    });
    builder.addCase(sendSlaEsignRequest.rejected, (state, action) => {
      state.loading = false;
      state.esignData = {};
      state.error = action.payload;
    });

    builder.addCase(generateRiskAgreement.pending, (state) => {
      state.riskAgreementLoading = true;
      state.error = null;
    });
    builder.addCase(generateRiskAgreement.fulfilled, (state, action) => {
      state.riskAgreementLoading = false;
      state.riskAgreement = action.payload;
      state.error = null;
    });
    builder.addCase(generateRiskAgreement.rejected, (state, action) => {
      state.riskAgreementLoading = false;
      state.riskAgreement = {};
      state.error = action.payload;
    });

    builder.addCase(sendRiskAgreementRequest.pending, (state) => {
      state.riskAgreementLoading = true;
      state.error = null;
    });
    builder.addCase(sendRiskAgreementRequest.fulfilled, (state, action) => {
      state.riskAgreementLoading = false;
      state.riskAgreementData = action.payload;
      state.error = null;
    });
    builder.addCase(sendRiskAgreementRequest.rejected, (state, action) => {
      state.riskAgreementLoading = false;
      state.riskAgreementData = {};
      state.error = action.payload;
    });

    builder.addCase(sendAgreementESignRequest.pending, (state) => {
      state.tripartyAgreeLoading = true;
      state.agreeReqError = null;
    });
    builder.addCase(sendAgreementESignRequest.fulfilled, (state, action) => {
      state.tripartyAgreeLoading = false;
      state.tripartyAgreeData = action.payload;
      state.agreeReqError = null;
    });
    builder.addCase(sendAgreementESignRequest.rejected, (state, action) => {
      state.tripartyAgreeLoading = false;
      state.tripartyAgreeData = {};
      state.agreeReqError = action.payload;
    });

    builder.addCase(getSlaSignStatus.pending, (state) => {
      state.checkStatusLoading = true;
      state.checkStatusError = null;
    });
    builder.addCase(getSlaSignStatus.fulfilled, (state, action) => {
      state.checkStatusLoading = false;
      state.checkStatusError = null;
    });
    builder.addCase(getSlaSignStatus.rejected, (state, action) => {
      state.checkStatusLoading = false;
      state.checkStatusError = action.payload;
    });

    builder.addCase(getAgreementEsignStatus.pending, (state) => {
      state.agreementSubmitLoading = true;
      state.agreementSubmitError = null;
    });
    builder.addCase(getAgreementEsignStatus.fulfilled, (state, action) => {
      state.agreementSubmitLoading = false;
      state.agreementSubmitError = null;
    });
    builder.addCase(getAgreementEsignStatus.rejected, (state, action) => {
      state.agreementSubmitLoading = false;
      state.agreementSubmitError = action.payload;
    });

    builder.addCase(getDocketInfo.pending, (state) => {
      state.docketInfoLoading = true;
      state.docketInfoError = null;
    });
    builder.addCase(getDocketInfo.fulfilled, (state, action) => {
      state.docketInfoLoading = false;
      state.docketInfoError = null;
    });
    builder.addCase(getDocketInfo.rejected, (state, action) => {
      state.docketInfoLoading = false;
      state.docketInfoError = action.payload;
    });
  },
  reducers: {},
});

export const {} = EsignSlice.actions;

export default EsignSlice.reducer;
